import React, { useCallback, useEffect, useRef, useState } from "react";
import { Search, Sparkles, Bot, BotOff, CheckCircle2, AlertTriangle, Ban, Send, Lock, FlaskConical } from "lucide-react";
import { PageHeader, Button, Input, Select, Textarea, StatusBadge, LoadingBlock, EmptyState, toast } from "@/components/ds";
import { api, formatApiError, type InboxConversation, type InboxDetail } from "@/lib/api";
import { STATUS_META, CHANNEL_META, fmtTime } from "./inbox/constants";
import { ConversationList } from "./inbox/ConversationList";
import { Thread } from "./inbox/Thread";
import { SidePanels } from "./inbox/SidePanels";
import { Inbox as InboxIcon } from "lucide-react";

const CHANNELS = [{ value: "all", label: "All channels" }, { value: "sms", label: "SMS" }, { value: "voice", label: "Voice" }, { value: "simulator", label: "Simulator" }];
const ASSIGNMENTS = [{ value: "all", label: "All" }, { value: "me", label: "Assigned to me" }, { value: "unassigned", label: "Unassigned" }];
const PERIODS = [
  { value: "today", label: "Today" }, { value: "yesterday", label: "Yesterday" },
  { value: "last_7_days", label: "Last 7 days" }, { value: "last_30_days", label: "Last 30 days" },
  { value: "last_90_days", label: "Last 90 days" }, { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" }, { value: "all_time", label: "All time" },
  { value: "custom", label: "Custom range…" },
];

export default function Inbox() {
  const [items, setItems] = useState<InboxConversation[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<InboxDetail | null>(null);
  const [q, setQ] = useState("");
  const [channel, setChannel] = useState("all");
  const [status, setStatus] = useState("all");
  const [assignment, setAssignment] = useState("all");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [period, setPeriod] = useState("last_30_days");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [counts, setCounts] = useState<{ total: number; calls: number; texts: number } | null>(null);
  const [statuses, setStatuses] = useState<{ value: string; label: string }[]>([]);
  const [composerTab, setComposerTab] = useState<"reply" | "note" | "simulate">("reply");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const threadEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => { api.inboxMeta().then((m) => setStatuses(m.statuses)).catch(() => {}); }, []);

  const loadList = useCallback(async () => {
    const params: Record<string, string> = {};
    if (q.trim()) params.q = q.trim();
    if (channel !== "all") params.channel = channel;
    if (status !== "all") params.status = status;
    if (assignment !== "all") params.assignment = assignment;
    if (unreadOnly) params.unread = "true";
    if (period) params.period = period;
    if (period === "custom") {
      if (!dateFrom || !dateTo) { setItems([]); setCounts({ total: 0, calls: 0, texts: 0 }); return; }
      params.date_from = dateFrom; params.date_to = dateTo;
    }
    try {
      const [list, cnt] = await Promise.all([api.inboxList(params), api.inboxCounts(params)]);
      setItems(list); setCounts(cnt);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [q, channel, status, assignment, unreadOnly, period, dateFrom, dateTo]);

  const loadDetail = useCallback(async (id: string) => {
    try { setDetail(await api.inboxGet(id)); } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { const t = setTimeout(loadList, 200); return () => clearTimeout(t); }, [loadList]);

  // Realtime-ready polling (a real provider can replace this later).
  useEffect(() => {
    const iv = setInterval(() => { loadList(); if (selectedId) loadDetail(selectedId); }, 5000);
    return () => clearInterval(iv);
  }, [loadList, loadDetail, selectedId]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    loadDetail(selectedId);
    api.inboxRead(selectedId).then(loadList).catch(() => {});
  }, [selectedId]); // eslint-disable-line

  useEffect(() => { threadEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [detail?.messages.length]);

  const conv = detail?.conversation;

  const act = async (fn: () => Promise<unknown>, msg?: string) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); if (selectedId) await loadDetail(selectedId); await loadList(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const send = async () => {
    if (!selectedId || !draft.trim()) return;
    const body = draft.trim();
    setBusy(true);
    try {
      if (composerTab === "reply") await api.inboxReply(selectedId, body);
      else if (composerTab === "note") await api.inboxNote(selectedId, body);
      else await api.inboxSimulate(selectedId, body, true);
      setDraft("");
      await loadDetail(selectedId); await loadList();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const seed = async () => {
    setSeeding(true);
    try { const r = await api.inboxSeed(); toast.success(`Loaded ${r.created} sample conversations`); await loadList(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSeeding(false); }
  };

  return (
    <div data-testid="page-inbox" className="space-y-4">
      <PageHeader title="Inbox" description="Every call and text in one place — SMS, Voice and the internal simulator." testid="inbox-header"
        action={<Button variant="outline" onClick={seed} disabled={seeding} data-testid="inbox-seed-btn"><Sparkles className="h-4 w-4" /> {seeding ? "Loading…" : "Load sample conversations"}</Button>} />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2" data-testid="inbox-filters">
        <div className="relative min-w-[220px] flex-1"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search conversations…" className="pl-9" data-testid="inbox-search" /></div>
        <div className="w-44"><Select value={period} onChange={setPeriod} options={PERIODS} testid="inbox-period-filter" /></div>
        <div className="w-40"><Select value={channel} onChange={setChannel} options={CHANNELS} testid="inbox-channel-filter" /></div>
        <div className="w-48"><Select value={status} onChange={setStatus} options={[{ value: "all", label: "All statuses" }, ...statuses]} testid="inbox-status-filter" /></div>
        <div className="w-44"><Select value={assignment} onChange={setAssignment} options={ASSIGNMENTS} testid="inbox-assignment-filter" /></div>
        <Button variant={unreadOnly ? "primary" : "outline"} size="sm" onClick={() => setUnreadOnly((v) => !v)} data-testid="inbox-unread-filter">Unread</Button>
      </div>

      {period === "custom" && (
        <div className="flex flex-wrap items-center gap-2" data-testid="inbox-custom-range">
          <span className="text-xs font-medium text-slate-500">From</span>
          <Input type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} className="w-40" data-testid="inbox-date-from" />
          <span className="text-xs font-medium text-slate-500">To</span>
          <Input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} className="w-40" data-testid="inbox-date-to" />
        </div>
      )}

      {counts && (
        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500" data-testid="inbox-result-counts">
          <span data-testid="inbox-count-total"><span className="font-semibold text-slate-900">{counts.total}</span> conversation{counts.total === 1 ? "" : "s"}</span>
          <span className="text-slate-300">·</span>
          <span data-testid="inbox-count-calls"><span className="font-semibold text-slate-700">{counts.calls}</span> calls</span>
          <span className="text-slate-300">·</span>
          <span data-testid="inbox-count-texts"><span className="font-semibold text-slate-700">{counts.texts}</span> texts</span>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[320px_1fr_300px]" style={{ minHeight: "60vh" }}>
        {/* List */}
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid="inbox-list-pane">
          {items === null ? <LoadingBlock rows={5} /> : <ConversationList items={items} selectedId={selectedId} onSelect={setSelectedId} />}
        </div>

        {/* Selected conversation */}
        <div className="flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid="inbox-thread-pane">
          {!conv ? (
            <div className="flex flex-1 items-center justify-center p-8"><EmptyState icon={InboxIcon} title="Select a conversation" description="Choose a conversation on the left, or load sample data to explore." /></div>
          ) : (
            <>
              <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3" data-testid="conversation-header">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {React.createElement(CHANNEL_META[conv.channel]?.icon ?? CHANNEL_META.sms.icon, { className: "h-4 w-4 text-slate-400" })}
                    <span className="truncate text-sm font-semibold text-slate-900">{conv.contact_name ?? "Unknown"}</span>
                    <StatusBadge tone={(STATUS_META[conv.status] ?? STATUS_META.open).tone}>{(STATUS_META[conv.status] ?? STATUS_META.open).label}</StatusBadge>
                  </div>
                  <p className="truncate text-xs text-slate-400">{conv.contact_phone ?? conv.customer_phone ?? ""} · {conv.subject}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <Button size="sm" variant={conv.ai_active ? "outline" : "primary"} onClick={() => act(() => api.inboxAiToggle(conv.id, !conv.ai_active), conv.ai_active ? "AI paused" : "AI resumed")} disabled={busy} data-testid="inbox-ai-toggle">
                    {conv.ai_active ? <><BotOff className="h-4 w-4" /> Pause AI</> : <><Bot className="h-4 w-4" /> Resume AI</>}
                  </Button>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-100 px-4 py-2" data-testid="conversation-actions">
                <Button size="sm" variant="outline" onClick={() => act(() => api.inboxResolve(conv.id), "Resolved")} disabled={busy} data-testid="action-resolve"><CheckCircle2 className="h-4 w-4" /> Resolve</Button>
                <Button size="sm" variant="outline" onClick={() => act(() => api.inboxEscalate(conv.id, "Escalated by staff"), "Escalated")} disabled={busy} data-testid="action-escalate"><AlertTriangle className="h-4 w-4" /> Escalate</Button>
                <Button size="sm" variant="outline" onClick={() => act(() => api.inboxSpam(conv.id), "Marked spam")} disabled={busy} data-testid="action-spam"><Ban className="h-4 w-4" /> Spam</Button>
                <div className="w-40"><Select value={conv.status} onChange={(s) => act(() => api.inboxStatus(conv.id, s), "Status updated")} options={statuses} testid="action-status-select" /></div>
                <Button size="sm" variant="outline" onClick={() => act(() => api.inboxAssign(conv.id, conv.assigned_to ? null : "self"), "Assignment updated")} disabled={busy} data-testid="action-assign">{conv.assigned_to ? "Unassign" : "Assign to me"}</Button>
              </div>

              <div className="flex-1 overflow-y-auto p-4" data-testid="thread-scroll">
                {detail && <Thread messages={detail.messages} callRecord={detail.call_record} />}
                <div ref={threadEndRef} />
              </div>

              {/* Composer */}
              <div className="border-t border-slate-100 p-3" data-testid="composer">
                <div className="mb-2 flex gap-1">
                  <button onClick={() => setComposerTab("reply")} data-testid="composer-tab-reply" className={`rounded-md px-3 py-1 text-xs font-medium ${composerTab === "reply" ? "bg-emerald-100 text-emerald-800" : "text-slate-500 hover:bg-slate-100"}`}>Staff reply</button>
                  <button onClick={() => setComposerTab("note")} data-testid="composer-tab-note" className={`flex items-center gap-1 rounded-md px-3 py-1 text-xs font-medium ${composerTab === "note" ? "bg-amber-100 text-amber-800" : "text-slate-500 hover:bg-slate-100"}`}><Lock className="h-3 w-3" /> Internal note</button>
                  <button onClick={() => setComposerTab("simulate")} data-testid="composer-tab-simulate" className={`flex items-center gap-1 rounded-md px-3 py-1 text-xs font-medium ${composerTab === "simulate" ? "bg-blue-100 text-blue-800" : "text-slate-500 hover:bg-slate-100"}`}><FlaskConical className="h-3 w-3" /> Simulate customer</button>
                </div>
                {composerTab === "note" && <p className="mb-1 text-[11px] text-amber-600">Internal notes are visible to staff only — never sent to the customer.</p>}
                {composerTab === "simulate" && <p className="mb-1 text-[11px] text-blue-600">Simulates an inbound customer message (Twilio not connected). The AI may auto-reply if active.</p>}
                <div className="flex items-end gap-2">
                  <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} data-testid="composer-input"
                    placeholder={composerTab === "reply" ? "Type a reply to the customer…" : composerTab === "note" ? "Add an internal note…" : "Type a message as the customer…"} />
                  <Button onClick={send} disabled={busy || !draft.trim()} data-testid="composer-send"><Send className="h-4 w-4" /> Send</Button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Side panels */}
        <div className="overflow-y-auto" data-testid="inbox-panels-pane">
          {detail ? <SidePanels detail={detail} /> : <div className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-xs text-slate-400">Contact, lead and appointment details appear here.</div>}
        </div>
      </div>
    </div>
  );
}
